/* ClientFolderHost (v4.10) — renders the active client folder overlay.
 *
 * Source of truth for the open tabs is the GlobalTabBar at the top of the
 * page. This host only renders the *active client folder* when an active
 * client is requested.
 *
 * Events:
 *   • open-client-folder              { contactId }  → load + show that client
 *   • global-tab-section-activated                   → hide overlay (section is now active)
 *   • global-tab-internal-close-client { contactId } → forget cached client
 *   • close-all-client-folders                       → drop everything
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { updateContact } from "@/lib/crmApi";
import { folderIsDirty, requestFolderClose } from "@/lib/clientFolderGuard";
import ClientDetailRaynet from "./crm/ClientDetailRaynet";
import { W_COLLAPSED, W_EXPANDED } from "./LeftSidebar";
import { contactFullName } from "@/lib/contactName";

export default function ClientFolderHost() {
  const navigate = useNavigate();
  // Cache of loaded contacts. Keyed by id. Only metadata for re-fetch handling.
  const [cache, setCache] = useState({});
  const cacheRef = useRef(cache);
  useEffect(() => { cacheRef.current = cache; }, [cache]);
  const [activeId, setActiveId] = useState(null);
  const activeIdRef = useRef(null);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  // Navigační kontrakt: požadovaná záložka (tab/section) + monotonní token, aby
  // se stejná sekce dala vyžádat opakovaně (nikdy no-op), + returnTo pro zavření.
  const [requestedTab, setRequestedTab] = useState(null);
  const [navToken, setNavToken] = useState(0);
  const returnToRef = useRef(null);

  // Sidebar width follows user lock state
  const [sidebarLocked, setSidebarLocked] = useState(() => localStorage.getItem("sidebar-locked") === "1");
  useEffect(() => {
    const onToggle = (e) => setSidebarLocked(!!e.detail?.locked);
    window.addEventListener("sidebar-toggle", onToggle);
    return () => window.removeEventListener("sidebar-toggle", onToggle);
  }, []);
  const sidebarWidth = sidebarLocked ? W_EXPANDED : W_COLLAPSED;
  // Na mobilu (< 1024 px, stejná hranice jako AppLayout) je sidebar off-canvas —
  // overlay musí začínat u levého okraje, jinak pod ním prosvítá pruh stránky (CRM-05).
  const [jeMobil, setJeMobil] = useState(() => typeof window !== "undefined" && window.innerWidth < 1024);
  useEffect(() => {
    const onResize = () => setJeMobil(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch + cache
  const fetchContact = useCallback(async (id) => {
    if (cacheRef.current[id]) return cacheRef.current[id];
    try {
      const { data } = await api.get(`/api/crm/contacts/${id}`);
      setCache((c) => ({ ...c, [id]: data }));
      return data;
    } catch {
      toast.error("Klienta se nepodařilo načíst");
      return null;
    }
  }, []);

  // Event wiring
  useEffect(() => {
    const doOpen = async (e) => {
      const id = e.detail?.contactId;
      const contact = await fetchContact(id);
      if (!contact) return;
      // Navigační kontrakt: přenes tab/section + returnTo. Token++ zajistí, že se
      // i stejná sekce (nebo stejný klient) dá vyžádat znovu bez no-op.
      setRequestedTab(e.detail?.tab || e.detail?.section || null);
      returnToRef.current = e.detail?.returnTo || null;
      setNavToken((n) => n + 1);
      setActiveId(id);
      // Make sure GlobalTabBar has this client tab
      window.dispatchEvent(new CustomEvent("global-tab-open-client", {
        detail: {
          contactId: id,
          meta: { name: contact?.name, surname: contact?.surname, stage: contact?.stage },
        },
      }));
    };
    const onOpen = (e) => {
      const id = e.detail?.contactId;
      if (!id) return;
      // Přepnutí na JINÉHO klienta s neuloženými změnami → dirty-guard (dialog); po
      // potvrzení re-dispatchni tentýž požadavek (guard je pak už čistý → projde).
      if (activeIdRef.current && activeIdRef.current !== id && folderIsDirty()) {
        const detail = e.detail;
        requestFolderClose(() => window.dispatchEvent(new CustomEvent("open-client-folder", { detail })));
        return;
      }
      doOpen(e);
    };
    const onSectionActivated = () => {
      requestFolderClose(() => setActiveId(null));
    };
    const onInternalClose = (e) => {
      const id = e.detail?.contactId;
      if (!id) return;
      const doClose = () => {
        setCache((c) => { const next = { ...c }; delete next[id]; return next; });
        setActiveId((cur) => (cur === id ? null : cur));
      };
      // Guard jen pro AKTIVNÍ složku (jiný tab se zavírá bez ptaní).
      if (id === activeIdRef.current) { requestFolderClose(doClose); return; }
      doClose();
    };
    const onCloseAll = () => {
      requestFolderClose(() => { setCache({}); setActiveId(null); });
    };
    window.addEventListener("open-client-folder", onOpen);
    window.addEventListener("global-tab-section-activated", onSectionActivated);
    window.addEventListener("global-tab-internal-close-client", onInternalClose);
    window.addEventListener("close-all-client-folders", onCloseAll);
    return () => {
      window.removeEventListener("open-client-folder", onOpen);
      window.removeEventListener("global-tab-section-activated", onSectionActivated);
      window.removeEventListener("global-tab-internal-close-client", onInternalClose);
      window.removeEventListener("close-all-client-folders", onCloseAll);
    };
  }, [fetchContact]);

  const activeContact = activeId ? cache[activeId] : null;

  const patchActiveContact = async (patch) => {
    if (!activeContact) return;
    try {
      // updateContact (crmApi) provede PUT a ZNEPLATNÍ cache "contacts" → ostatní
      // stránky (Klienti/Dashboard/FamilyPicker…) neuvidí stará data (P2 fix).
      const data = await updateContact(activeContact.id, patch);
      setCache((c) => ({ ...c, [activeContact.id]: data }));
      // Update meta in global tab bar (e.g., stage changed)
      window.dispatchEvent(new CustomEvent("global-tab-update-client-meta", {
        detail: {
          contactId: activeContact.id,
          meta: { name: data?.name, surname: data?.surname, stage: data?.stage },
        },
      }));
      // Klienti list se po úpravě z overlay musí obnovit (P1 fix — jinak zůstane stale).
      window.dispatchEvent(new CustomEvent("client-list-refresh"));
    } catch (err) {
      toast.error("Uložení se nezdařilo: " + (err?.response?.data?.detail || ""));
      throw err;
    }
  };

  const deleteActiveContact = async () => {
    if (!activeContact) return;
    if (!window.confirm(`Přesunout klienta ${contactFullName(activeContact)} do koše? Kdykoli ho můžeš obnovit.`)) return;
    try {
      // DELETE je nově SOFT — backend nastaví archived=true (koš), nemaže dokument
      await api.delete(`/api/crm/contacts/${activeContact.id}`);
      toast.success("Klient je v koši — obnovíš ho v Klienti → Koš");
      window.dispatchEvent(new CustomEvent("global-tab-close-client", { detail: { contactId: activeContact.id } }));
      window.dispatchEvent(new CustomEvent("client-list-refresh"));
    } catch {
      toast.error("Smazání se nezdařilo");
    }
  };

  const signActiveContact = async () => {
    if (!activeContact) return;
    await patchActiveContact({ stage: "signed", sign_date: new Date().toISOString().slice(0, 10) });
    toast.success("Podpis zaznamenán");
  };

  const closeActive = () => {
    if (!activeId) return;
    const returnTo = returnToRef.current;
    returnToRef.current = null;
    // Tell GlobalTabBar to close this tab
    window.dispatchEvent(new CustomEvent("global-tab-close-client", { detail: { contactId: activeId } }));
    // Navigační kontrakt: pokud volající předal returnTo, vrať se tam po zavření.
    if (returnTo) navigate(returnTo);
  };

  if (!activeContact) return null;

  return (
    <AnimatePresence>
      <motion.div
        key={`client-overlay-${activeContact.id}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed top-[42px] right-0 bottom-0 z-[78] flex flex-col"
        style={{
          background: "var(--bg, #FAF7F3)",
          left: jeMobil ? 0 : sidebarWidth,
          transition: "left 0.22s cubic-bezier(0.32, 0.72, 0, 1)",
        }}
        data-testid="client-folder-overlay"
      >
        <div className="flex-1 min-h-0 overflow-hidden">
          <ClientDetailRaynet
            key={activeContact.id}
            contact={activeContact}
            initialTab={requestedTab}
            navToken={navToken}
            onClose={closeActive}
            onPatch={patchActiveContact}
            onDelete={deleteActiveContact}
            onSign={signActiveContact}
            fullScreen
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
